import { useCallback, useEffect, useRef, useState } from 'react';

import { getData } from 'services/api';

import { ticketPdfEndpoints } from './ticketPdfEndpoints';
import { PrintableTickets, PrintFailure, PrintFormat } from './types';

export interface UsePrintTicketsOptions {
	onError?: (reason: PrintFailure) => void;
}

export interface UsePrintTicketsReturn {
	printingKey: string | null;
	isPrinting: boolean;
	print: (key: string, tickets: PrintableTickets, format: PrintFormat) => Promise<void>;
}

interface PrintArtifacts {
	frames: HTMLIFrameElement[];
	urls: string[];
}

const mountPrintFrame = (
	blob: Blob,
	artifacts: PrintArtifacts,
	onError?: (reason: PrintFailure) => void
): Promise<void> =>
	new Promise((resolve, reject) => {
		const url = window.URL.createObjectURL(blob);
		const iframe = document.createElement('iframe');
		iframe.style.position = 'fixed';
		iframe.style.right = '0';
		iframe.style.bottom = '0';
		iframe.style.width = '0';
		iframe.style.height = '0';
		iframe.style.border = '0';
		iframe.setAttribute('aria-hidden', 'true');
		iframe.src = url;
		iframe.onload = () => {
			try {
				iframe.contentWindow?.focus();
				iframe.contentWindow?.print();
			} catch {
				onError?.('dialog');
			}
			resolve();
		};
		iframe.onerror = () => {
			window.URL.revokeObjectURL(url);
			iframe.remove();
			reject(new Error('print frame failed to load'));
		};
		document.body.appendChild(iframe);
		artifacts.frames.push(iframe);
		artifacts.urls.push(url);
	});

export const usePrintTickets = ({ onError }: UsePrintTicketsOptions = {}): UsePrintTicketsReturn => {
	const [printingKey, setPrintingKey] = useState<string | null>(null);
	const artifactsRef = useRef<PrintArtifacts>({ frames: [], urls: [] });
	const mountedRef = useRef(true);

	const release = useCallback(() => {
		const artifacts = artifactsRef.current;
		artifacts.urls.forEach((url) => window.URL.revokeObjectURL(url));
		artifacts.frames.forEach((frame) => frame.remove());
		artifactsRef.current = { frames: [], urls: [] };
	}, []);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			release();
		};
	}, [release]);

	const print = useCallback(
		async (key: string, tickets: PrintableTickets, format: PrintFormat) => {
			const endpoints = ticketPdfEndpoints(tickets, format);
			if (endpoints.length === 0) {
				onError?.('unavailable');
				return;
			}

			// Released here, not after the last print: tearing a frame down while its dialog is still
			// reading the blob cancels the print in some browsers.
			release();
			setPrintingKey(key);
			try {
				for (const endpoint of endpoints) {
					const blob = (await getData(endpoint, {
						responseType: 'blob',
						headers: { Accept: 'application/pdf' },
					})) as Blob;
					// The surface can unmount mid-fetch. Mounting a frame now would append an iframe and
					// an object URL that no later cleanup runs to release.
					if (!mountedRef.current) return;
					await mountPrintFrame(blob, artifactsRef.current, onError);
				}
			} catch {
				onError?.('fetch');
			} finally {
				if (mountedRef.current) setPrintingKey(null);
			}
		},
		[onError, release]
	);

	return { printingKey, isPrinting: printingKey !== null, print };
};
